import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSyncWatch } from "../context/SyncContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { AuthImg } from "../components/AuthImg";
import { Switch } from "../components/ui/switch";
import { UserRound, Plus, Search, Download, FileText, KeyRound, Power, Trash2, AlertCircle, LayoutGrid, List as ListIcon, Upload } from "lucide-react";
import PersonAlertBanners from "./personal/PersonAlertBanners";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { exportCSV } from "../lib/csvExport";
import { exportBrandedPDF } from "../lib/pdfExport";
import { useConfirm } from "../components/ConfirmProvider";
import { fmtRoDate } from "../lib/dateFmt";
import MeldungBulkUploadDialog from "../components/MeldungBulkUploadDialog";
import { OnlineBadge } from "../components/OnlineBadge";
import PSAAssignmentPanel from "../components/PSAAssignmentPanel";

const EMPTY_FORM = {
  nume: "", prenume: "", data_nasterii: "", adresa: "", telefon: "",
  data_angajarii: "", data_concedierii: "",
  permis_conducere: false,
  ekas: false,
  limba_germana: false,
  is_subcontractor: false,
  office_staff: false,
  cazare_type: "own",
  rental_id: null,
  adresa_cazare: "",
  chei_cazare: "",
  bila_rosie: ["", "", ""],
  echipament: "", alte_detalii: "",
  hourly_rate: "",
  monthly_salary: "",
  nr_copii: "",
  firma_id: null,
  active: true,
};

// Câmpuri esențiale pentru o persoană — semnalate când lipsesc
const ESSENTIAL_PERSON_FIELDS = [
  { key: "nume", label: "Nume" },
  { key: "prenume", label: "Prenume" },
  { key: "data_nasterii", label: "Data nașterii" },
  { key: "telefon", label: "Telefon" },
  { key: "adresa", label: "Adresă" },
  { key: "data_angajarii", label: "Data angajării" },
];

export function missingPersonFields(person) {
  if (!person) return [];
  return ESSENTIAL_PERSON_FIELDS.filter((f) => {
    const v = person[f.key];
    return !v || String(v).trim() === "";
  });
}

// Documente obligatorii pe persoană:
//   - Buletin / Carte de identitate — OBLIGATORIU pentru TOATE persoanele (inclusiv subcontractori)
//   - Contract de muncă — doar ConVic
//   - Permis de ședere elvețian (cu data expirării) — doar ConVic
//   - Kündigung — doar ConVic dacă persoana e concediată
// Verificăm case-insensitive după doc_type SAU nume fișier (pentru compatibilitate cu intrările vechi).
export function missingPersonDocs(person, employerCompanies = []) {
  if (!person) return [];
  const reConvic = /^convic/i;
  const internalIds = new Set((employerCompanies || []).filter((c) => reConvic.test(c.name || "")).map((c) => c.id));
  const isConvicEmployee = !person.firma_id || internalIds.has(person.firma_id);
  const docs = person.documente || [];
  const missing = [];
  const has = (re) => docs.some((d) => (d?.doc_type && re.test(d.doc_type)) || (d?.name && re.test(d.name)));
  const findDoc = (re) => docs.find((d) => (d?.doc_type && re.test(d.doc_type)) || (d?.name && re.test(d.name)));
  // 1) Buletin/CI — OBLIGATORIU TOATĂ LUMEA (angajați proprii + subcontractori).
  // Acceptă și „Pașaport" ca alternativă (pentru cetățenii non-UE).
  const reBuletin = /buletin|carte\s*de?\s*identitate|\bid\b|\bci\b|pa[șs]aport|passport|reisepass/i;
  if (!has(reBuletin)) missing.push({ key: "buletin", label: "Buletin / Carte de identitate" });
  // Documentele restante sunt verificate DOAR pentru angajații proprii ConVic.
  if (!isConvicEmployee) return missing;
  const reContract = /contract\s*de?\s*munc/i;
  if (!has(reContract)) missing.push({ key: "contract_munca", label: "Contract de muncă" });
  // Permis de ședere elvețian — obligatoriu cu data expirării
  // Acceptă diacritice (ș/ş/ş) și ortografii cu / fără diacritic
  const rePermis = /permis\s*(de\s*)?([sșş][eă]?dere|aufenthalt)|aufenthaltsbewilligung|ausweis\s*[bclg]/i;
  const permis = findDoc(rePermis);
  if (!permis) {
    missing.push({ key: "permis_sedere", label: "Permis de ședere elvețian" });
  } else if (!permis.expires_at) {
    missing.push({ key: "permis_sedere_expira", label: "Permis de ședere — data expirării" });
  } else {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exp = new Date(permis.expires_at);
    if (!isNaN(exp.getTime()) && exp < today) {
      missing.push({ key: "permis_sedere_expirat", label: "Permis de ședere — EXPIRAT" });
    }
  }
  // Kündigung obligatoriu doar dacă persoana e concediată
  if (person.data_concedierii) {
    const reKundigung = /k[üu]ndigung/i;
    if (!has(reKundigung)) missing.push({ key: "kundigung", label: "Kündigung (decizie de concediere)" });
  }
  return missing;
}

// Helper: obține lista de permise care expiră în următoarele N zile (sau au expirat deja)
export function permisExpiringSoon(persons, employerCompanies, withinDays = 14) {
  const reConvic = /^convic/i;
  const internalIds = new Set((employerCompanies || []).filter((c) => reConvic.test(c.name || "")).map((c) => c.id));
  const rePermis = /permis\s*(de\s*)?([sșş][eă]?dere|aufenthalt)|aufenthaltsbewilligung|ausweis\s*[bclg]/i;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const out = [];
  for (const p of (persons || [])) {
    if (p.active === false || p.data_concedierii) continue;
    const isConvic = !p.firma_id || internalIds.has(p.firma_id);
    if (!isConvic) continue;
    const docs = p.documente || [];
    const permis = docs.find((d) => (d?.doc_type && rePermis.test(d.doc_type)) || (d?.name && rePermis.test(d.name)));
    if (!permis || !permis.expires_at) continue;
    const exp = new Date(permis.expires_at);
    if (isNaN(exp.getTime())) continue;
    const daysLeft = Math.floor((exp - today) / (24 * 3600 * 1000));
    if (daysLeft <= withinDays) {
      out.push({ person_id: p.id, person_name: `${p.nume || ""} ${p.prenume || ""}`.trim(), expires_at: permis.expires_at, days_left: daysLeft });
    }
  }
  out.sort((a, b) => a.days_left - b.days_left);
  return out;
}

export default function Personal() {
  const confirm = useConfirm();
  const { canDo, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [employerCompanies, setEmployerCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const currentMonth = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const [salaryMonth, setSalaryMonth] = useState(currentMonth);
  const [filter, setFilter] = useState("");
  const [firmaFilter, setFirmaFilter] = useState("all"); // "all" | "none" | firma_id
  const [noRateFilter, setNoRateFilter] = useState(false);
  const [ekasFilter, setEkasFilter] = useState(false);
  const [licenseFilter, setLicenseFilter] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [auditCoverage, setAuditCoverage] = useState({}); // person_id → {missing_months: [...], coverage_pct, employed_since}
  const [meldungenSummary, setMeldungenSummary] = useState({}); // person_id → {used_days, remaining_days}
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data }, r, ec, u, sp, ac, ms] = await Promise.all([
        api.get("/persons-overview"),
        api.get("/rentals").catch(() => ({ data: [] })),
        api.get("/employer-companies").catch(() => ({ data: [] })),
        isAdmin ? api.get("/users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        api.get(`/salary-payments?month=${salaryMonth}`).catch(() => ({ data: [] })),
        isAdmin ? api.get("/admin/audit-coverage").catch(() => ({ data: { rows: [] } })) : Promise.resolve({ data: { rows: [] } }),
        api.get("/meldungen/summary").catch(() => ({ data: { rows: [] } })),
      ]);
      setRows(data || []);
      setRentals(r.data || []);
      setEmployerCompanies(ec.data || []);
      setUsers(u.data || []);
      setSalaryPayments(sp.data || []);
      const map = {};
      for (const row of (ac.data?.rows || [])) {
        map[row.person_id] = row;
      }
      setAuditCoverage(map);
      const mmap = {};
      for (const row of (ms.data?.rows || [])) {
        mmap[row.person_id] = row;
      }
      setMeldungenSummary(mmap);
    } catch {}
  };

  // Reload payments when month changes
  useEffect(() => {
    api.get(`/salary-payments?month=${salaryMonth}`).then(({ data }) => setSalaryPayments(data || [])).catch(() => {});
  }, [salaryMonth]);

  const cyclePayment = async (personId) => {
    if (!isAdmin) return;
    const current = salaryPayments.find((p) => p.person_id === personId);
    const cur = current?.status || "unpaid";
    const next = cur === "unpaid" ? "partial" : (cur === "partial" ? "paid" : "unpaid");
    try {
      const { data } = await api.put(`/salary-payments/${personId}/${salaryMonth}`, { status: next });
      setSalaryPayments((prev) => {
        const others = prev.filter((p) => p.person_id !== personId);
        return [...others, data];
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const toggleActive = async (p) => {
    if (!canDo("PERSONAL", "edit")) { toast.error("Nu ai drept de editare"); return; }
    const newActive = p.active === false ? true : false;
    const action = newActive ? "ACTIVA" : "DEZACTIVA";
    if (!(await confirm({ body: `${action} ${p.nume} ${p.prenume}?`, danger: true }))) return;
    try {
      await api.put(`/persons/${p.id}`, { ...p, active: newActive });
      setRows((prev) => prev.map((x) => x.id === p.id ? { ...x, active: newActive } : x));
      toast.success(newActive ? "Persoană reactivată" : "Persoană dezactivată");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const removePerson = async (p) => {
    if (!canDo("PERSONAL", "delete")) { toast.error("Nu ai drept de ștergere"); return; }
    if (!(await confirm({ body: `ȘTERGE definitiv ${p.nume} ${p.prenume}?\n\nAceastă acțiune NU poate fi anulată. Toate datele asociate (calendar, ACONT, documente) vor fi șterse.`, danger: true }))) return;
    try {
      await api.delete(`/persons/${p.id}`);
      setRows((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Persoană ștearsă");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };
  useEffect(() => { load(); }, []);
  useSyncWatch(["persons", "vacations"], () => { load(); });

  // Auto-open add dialog when navigated with #new
  useEffect(() => {
    if (window.location.hash === "#new" && canDo("PERSONAL", "add")) {
      setOpen(true);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [canDo]);

  const [sortMode, setSortMode] = useState(() => localStorage.getItem("convic_persons_sort") || "convic_first");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("convic_persons_view") || "cards");

  useEffect(() => {
    localStorage.setItem("convic_persons_sort", sortMode);
  }, [sortMode]);

  const filtered = rows.filter((r) => {
    const q = filter.toLowerCase();
    if (q && !`${r.nume || ""} ${r.prenume || ""} ${r.telefon || ""}`.toLowerCase().includes(q)) return false;
    if (noRateFilter) {
      const isActive = r.active !== false && !r.data_concedierii;
      const noRate = r.hourly_rate == null || Number(r.hourly_rate) <= 0;
      if (!(isActive && noRate)) return false;
    }
    if (ekasFilter && !r.ekas) return false;
    if (licenseFilter && !r.permis_conducere) return false;
    if (firmaFilter === "all") return true;
    if (firmaFilter === "none") return !r.firma_id;
    return r.firma_id === firmaFilter;
  });

  const noRateCount = React.useMemo(
    () => rows.filter((r) => r.active !== false && !r.data_concedierii && (r.hourly_rate == null || Number(r.hourly_rate) <= 0)).length,
    [rows]
  );

  const sortedFiltered = React.useMemo(() => {
    const list = [...filtered];
    const fullName = (p) => `${p.nume || ""} ${p.prenume || ""}`.trim().toLowerCase();
    const firmaName = (p) =>
      (employerCompanies.find((c) => c.id === p.firma_id)?.name || "").toLowerCase();
    const isConVic = (p) => firmaName(p).includes("convic");

    switch (sortMode) {
      case "name_asc":
        list.sort((a, b) => fullName(a).localeCompare(fullName(b), "ro"));
        break;
      case "name_desc":
        list.sort((a, b) => fullName(b).localeCompare(fullName(a), "ro"));
        break;
      case "hire_desc":
        list.sort((a, b) => (b.data_angajarii || "").localeCompare(a.data_angajarii || ""));
        break;
      case "hire_asc":
        list.sort((a, b) => (a.data_angajarii || "").localeCompare(b.data_angajarii || ""));
        break;
      case "firma":
        list.sort((a, b) => {
          const fa = firmaName(a) || "zzz";
          const fb = firmaName(b) || "zzz";
          if (fa !== fb) return fa.localeCompare(fb, "ro");
          return fullName(a).localeCompare(fullName(b), "ro");
        });
        break;
      case "convic_first":
      default:
        list.sort((a, b) => {
          const ca = isConVic(a) ? 0 : 1;
          const cb = isConVic(b) ? 0 : 1;
          if (ca !== cb) return ca - cb;
          return fullName(a).localeCompare(fullName(b), "ro");
        });
        break;
    }
    return list;
  }, [filtered, sortMode, employerCompanies]);

  const firmaCounts = React.useMemo(() => {
    const counts = { all: rows.length, none: 0 };
    for (const c of employerCompanies) counts[c.id] = 0;
    for (const r of rows) {
      if (!r.firma_id) counts.none += 1;
      else if (counts[r.firma_id] !== undefined) counts[r.firma_id] += 1;
    }
    return counts;
  }, [rows, employerCompanies]);

  const submit = async () => {
    // Validare: toate câmpurile obligatorii trebuie completate
    const missing = [];
    const isEmpty = (v) => v === undefined || v === null || String(v).trim() === "";
    if (isEmpty(form.nume)) missing.push("Nume");
    if (isEmpty(form.prenume)) missing.push("Prenume");
    if (isEmpty(form.data_nasterii)) missing.push("Data nașterii");
    if (isEmpty(form.telefon)) missing.push("Telefon");
    if (isEmpty(form.adresa)) missing.push("Adresă");
    if (isEmpty(form.data_angajarii)) missing.push("Data angajării");
    if (!form.firma_id) missing.push("Firma angajatoare");
    if (isEmpty(form.hourly_rate) || Number(form.hourly_rate) <= 0) missing.push("Tarif orar");
    if (form.cazare_type === "rental" && !form.rental_id) missing.push("Selectează cazarea (Chirii)");
    if (form.cazare_type === "other" && isEmpty(form.adresa_cazare)) missing.push("Adresa cazare");
    if (missing.length > 0) {
      setAttemptedSubmit(true);
      toast.error(`Completează câmpurile obligatorii: ${missing.join(", ")}`);
      return;
    }
    try {
      const payload = {
        ...form,
        chei_cazare: parseInt(form.chei_cazare || 0) || 0,
        nr_copii: parseInt(form.nr_copii || 0) || 0,
        hourly_rate: form.hourly_rate === "" || form.hourly_rate === null ? null : (parseFloat(form.hourly_rate) || 0),
        monthly_salary: form.monthly_salary === "" || form.monthly_salary === null ? null : (parseFloat(form.monthly_salary) || 0),
        bila_rosie: (form.bila_rosie || []).filter((x) => x && x.trim()),
      };
      let res;
      try {
        res = await api.post("/persons", payload);
      } catch (e) {
        // 409 = deja există o persoană cu același nume + prenume; oferim opțiune bypass
        if (e?.response?.status === 409) {
          const msg = e?.response?.data?.detail || "Există deja o persoană cu același nume.";
          if (window.confirm(`${msg}\n\nEȘTI SIGUR că e o altă persoană cu același nume și vrei să o adaugi oricum?`)) {
            res = await api.post("/persons?force=1", payload);
          } else {
            return;
          }
        } else {
          throw e;
        }
      }
      const acc = res?.data?.auto_account;
      if (acc && acc.created) {
        toast.success(
          `Persoană adăugată. Cont creat: ${acc.username} / parolă: ${acc.password}`,
          { duration: 12000 },
        );
        // Copy credentials to clipboard for admin convenience
        try {
          await navigator.clipboard.writeText(`User: ${acc.username}\nParolă: ${acc.password}`);
        } catch { /* no-op */ }
      } else {
        toast.success("Persoană adăugată");
        if (acc && !acc.created && acc.error) {
          toast.error(`Contul nu a fost creat automat: ${acc.error}`, { duration: 8000 });
        }
      }
      setOpen(false);
      setForm(EMPTY_FORM);
      setAttemptedSubmit(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    }
  };

  const setF = (k, v) => setForm((x) => ({ ...x, [k]: v }));

  // Helpers pentru validare obligatorie pe formularul nou
  const isEmpty = (v) => v === undefined || v === null || String(v).trim() === "";
  const errBorder = (cond) => (attemptedSubmit && cond) ? "border-red-500 focus-visible:ring-red-500" : "";
  const ReqMark = () => <span className="text-red-600 ml-0.5">*</span>;

  return (
    <div data-testid="page-personal">
      <div className="flex items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Personal</h1>
          <p className="text-sm text-zinc-500 mt-1">Profiluri angajați, documente și calendar ore</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="export-personal-csv"
            onClick={() => exportCSV(sortedFiltered, [
              { key: "nume", label: "Nume" },
              { key: "prenume", label: "Prenume" },
              { key: "data_nasterii", label: "Data nașterii" },
              { key: "telefon", label: "Telefon" },
              { key: (r) => {
                const f = employerCompanies.find((c) => c.id === r.firma_id);
                return f ? f.name : "";
              }, label: "Firma" },
              { key: "data_angajarii", label: "Data angajării" },
              { key: "adresa_cazare", label: "Cazare" },
              { key: "echipament", label: "Echipament" },
              { key: (r) => (Array.isArray(r.bila_rosie) ? r.bila_rosie.filter((x) => x && x.trim()).join(" | ") : ""), label: "Bilă Roșie" },
              { key: (r) => {
                const u = users.find((x) => x.person_id === r.id);
                if (!u) return "fără cont";
                return u.active === false ? `${u.username} (oprit)` : u.username;
              }, label: "Cont" },
              { key: (r) => (r.active === false ? "Inactiv" : "Activ"), label: "Status" },
            ], "personal")}
          >
            <Download size={16} className="mr-2" /> Export CSV
          </Button>
          <Button
            variant="outline"
            data-testid="export-personal-pdf"
            onClick={async () => {
              try {
                await exportBrandedPDF({
                  title: "Raport personal",
                  subtitle: `${sortedFiltered.length} ${sortedFiltered.length === 1 ? "persoană" : "persoane"}${sortedFiltered.length !== rows.length ? ` (filtrat din ${rows.length})` : ""}`,
                  sections: [{
                    table: {
                      columns: ["Nume", "Telefon", "Firma", "Cazare", "Angajare", "Cont", "Status", "Bilă R."],
                      rows: sortedFiltered.map((p) => {
                        const f = employerCompanies.find((c) => c.id === p.firma_id);
                        const u = users.find((x) => x.person_id === p.id);
                        const acc = !u ? "fără cont" : (u.active === false ? `${u.username} (oprit)` : u.username);
                        return [
                          `${p.nume || ""} ${p.prenume || ""}`.trim(),
                          p.telefon || "—",
                          f ? f.name : "—",
                          p.adresa_cazare || "—",
                          fmtRoDate(p.data_angajarii) || "—",
                          acc,
                          p.active === false ? "Inactiv" : "Activ",
                          Array.isArray(p.bila_rosie) ? p.bila_rosie.filter((x) => x && x.trim()).length : 0,
                        ];
                      }),
                    },
                  }],
                  filename: `personal-${new Date().toISOString().slice(0, 10)}.pdf`,
                });
                toast.success("PDF generat");
              } catch { toast.error("Eroare PDF"); }
            }}
          >
            <FileText size={16} className="mr-2" /> Export PDF
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              data-testid="conformitate-documente-pdf"
              className="border-red-300 text-red-700 hover:bg-red-50"
              onClick={async () => {
                try {
                  const res = await api.get("/admin/conformitate-documente.pdf", { responseType: "blob" });
                  const url = window.URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `conformitate-documente-${new Date().toISOString().slice(0, 10)}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                  toast.success("Raport descărcat");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Eroare la generare PDF");
                }
              }}
              title="Listă PDF cu toți angajații care au documente lipsă (buletin, contract, Kündigung)"
            >
              <AlertCircle size={16} className="mr-2" /> Conformitate documente
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              data-testid="bulk-meldung-upload-btn"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => setBulkUploadOpen(true)}
              title="Încarcă un document Meldeverfahren — OCR-ul extrage toți angajații + intervalele"
            >
              <Upload size={16} className="mr-2" /> Adaugă Meldeverfahren
            </Button>
          )}
          {canDo("PERSONAL", "add") && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setAttemptedSubmit(false); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-person-btn" className="bg-[var(--primary)] hover:bg-[var(--primary-hover)]">
                <Plus size={16} className="mr-2" /> Adaugă persoană
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Persoană nouă</DialogTitle>
                <p className="text-xs text-zinc-500 mt-1">Câmpurile marcate cu <span className="text-red-600 font-bold">*</span> sunt obligatorii.</p>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Nume<ReqMark/></Label><Input data-testid="new-person-nume" className={errBorder(isEmpty(form.nume))} value={form.nume} onChange={(e) => setF("nume", e.target.value)} /></div>
                <div><Label>Prenume<ReqMark/></Label><Input data-testid="new-person-prenume" className={errBorder(isEmpty(form.prenume))} value={form.prenume} onChange={(e) => setF("prenume", e.target.value)} /></div>
                <div><Label>Data nașterii<ReqMark/></Label><Input type="date" className={errBorder(isEmpty(form.data_nasterii))} value={form.data_nasterii} onChange={(e) => setF("data_nasterii", e.target.value)} /></div>
                <div><Label>Telefon<ReqMark/></Label><Input className={errBorder(isEmpty(form.telefon))} value={form.telefon} onChange={(e) => setF("telefon", e.target.value)} /></div>
                <div className="md:col-span-2"><Label>Adresă<ReqMark/></Label><Input className={errBorder(isEmpty(form.adresa))} value={form.adresa} onChange={(e) => setF("adresa", e.target.value)} /></div>
                <div><Label>Data angajării<ReqMark/></Label><Input type="date" className={errBorder(isEmpty(form.data_angajarii))} value={form.data_angajarii} onChange={(e) => setF("data_angajarii", e.target.value)} /></div>

                <div className="flex items-center gap-3 py-2">
                  <Label className="small-label">Permis Conducere</Label>
                  <Switch checked={!!form.permis_conducere} onCheckedChange={(v) => setF("permis_conducere", v)} />
                  <span className="text-sm">{form.permis_conducere ? "DA" : "NU"}</span>
                </div>
                <div className="flex items-center gap-3 py-2">
                  <Label className="small-label">EKAS</Label>
                  <Switch checked={!!form.ekas} onCheckedChange={(v) => setF("ekas", v)} data-testid="form-ekas-toggle" />
                  <span className="text-sm inline-flex items-center gap-1">{form.ekas ? <><span className="text-yellow-500">⭐</span> DA</> : "NU"}</span>
                </div>
                <div className="flex items-center gap-3 py-2">
                  <Label className="small-label">Limba germană</Label>
                  <Switch checked={!!form.limba_germana} onCheckedChange={(v) => setF("limba_germana", v)} data-testid="form-limba-germana-toggle" />
                  <span className="text-sm inline-flex items-center gap-1">{form.limba_germana ? <><span>🇩🇪</span> DA</> : "NU"}</span>
                </div>
                <div className="flex items-center gap-3 py-2" title="Angajat al unui subcontractor — supus regulii Meldeverfahren (max 90 zile lucrătoare/an în CH)">
                  <Label className="small-label">Subcontractor (Meldeverfahren)</Label>
                  <Switch checked={!!form.is_subcontractor} onCheckedChange={(v) => setF("is_subcontractor", v)} data-testid="form-subcontractor-toggle" />
                  <span className="text-sm inline-flex items-center gap-1">{form.is_subcontractor ? <><span>🌍</span> DA</> : "NU"}</span>
                </div>
                <div className="flex items-center gap-3 py-2" title="Personal de birou — nu se numără la «disponibili pentru muncă» în Management / Concediu">
                  <Label className="small-label">Personal birou</Label>
                  <Switch checked={!!form.office_staff} onCheckedChange={(v) => setF("office_staff", v)} data-testid="form-office-staff-toggle" />
                  <span className="text-sm inline-flex items-center gap-1">{form.office_staff ? <><span>🏢</span> DA — exclus din disponibili</> : "NU"}</span>
                </div>
                <div className="flex items-center gap-3 py-2">
                  <Label className="small-label">Activ</Label>
                  <Switch checked={form.active !== false} onCheckedChange={(v) => setF("active", v)} />
                </div>

                <div>
                  <Label>Număr copii</Label>
                  <Input
                    type="number" min={0} step={1}
                    className="mt-1"
                    value={form.nr_copii}
                    onChange={(e) => setF("nr_copii", e.target.value)}
                    placeholder="0"
                    data-testid="new-person-nr-copii"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Firma angajatoare<ReqMark/></Label>
                  <Select value={form.firma_id || "__none__"} onValueChange={(v) => {
                    const newFid = v === "__none__" ? null : v;
                    const firm = employerCompanies.find((c) => c.id === newFid);
                    const isConvic = !firm || /^convic/i.test(firm.name || "");
                    setForm((f) => ({ ...f, firma_id: newFid, is_subcontractor: !isConvic }));
                  }}>
                    <SelectTrigger className={`mt-1 ${errBorder(!form.firma_id)}`} data-testid="new-person-firma"><SelectValue placeholder={employerCompanies.length === 0 ? "Adaugă firme în Setări → Firme angajatori" : "Selectează firma"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Niciuna —</SelectItem>
                      {employerCompanies.filter((c) => c.active !== false).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-zinc-500 mt-1">Pentru orice firmă diferită de „ConVic", se activează automat tracking-ul Meldeverfahren.</p>
                </div>

                <div className="md:col-span-2 pt-2 border-t border-[var(--border)] grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Tarif orar (CHF/h)<ReqMark/></Label>
                    <Input
                      type="number" step={0.5} min={0}
                      className={`mt-1 ${errBorder(isEmpty(form.hourly_rate) || Number(form.hourly_rate) <= 0)}`}
                      value={form.hourly_rate}
                      onChange={(e) => setF("hourly_rate", e.target.value)}
                      placeholder="ex: 25"
                      data-testid="new-person-hourly-rate"
                    />
                    <p className="text-[11px] text-zinc-500 mt-1">Va fi auto-completat la introducerea orelor în Calendar.</p>
                  </div>
                  <div>
                    <Label>Salariu lunar (CHF)</Label>
                    <Input
                      type="number" step={50} min={0}
                      className="mt-1"
                      value={form.monthly_salary ?? ""}
                      onChange={(e) => setF("monthly_salary", e.target.value)}
                      placeholder="ex: 4500"
                      data-testid="new-person-monthly-salary"
                    />
                    <p className="text-[11px] text-zinc-500 mt-1">Informativ. Opțional — completează doar dacă persoana e plătită cu salariu fix lunar.</p>
                  </div>
                </div>

                <div className="md:col-span-2 pt-2 border-t border-[var(--border)]">
                  <div className="small-label mb-2">Cazare</div>
                </div>
                <div className="md:col-span-2">
                  <Label>Tip cazare</Label>
                  <Select value={form.cazare_type} onValueChange={(v) => {
                    setForm((x) => ({ ...x, cazare_type: v, rental_id: null, adresa_cazare: v === "own" ? "" : x.adresa_cazare }));
                  }}>
                    <SelectTrigger className="mt-1" data-testid="new-person-cazare-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rental">Cazare din Chirii</SelectItem>
                      <SelectItem value="other">Altă adresă</SelectItem>
                      <SelectItem value="own">Adresă proprie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.cazare_type === "rental" && (
                  <div className="md:col-span-2">
                    <Label>Selectează cazarea (Chirii)<ReqMark/></Label>
                    <Select value={form.rental_id || ""} onValueChange={(v) => {
                      const r = rentals.find((x) => x.id === v);
                      setForm((x) => ({ ...x, rental_id: v, adresa_cazare: r ? r.adresa : "" }));
                    }}>
                      <SelectTrigger className={`mt-1 ${errBorder(!form.rental_id)}`} data-testid="new-person-rental"><SelectValue placeholder={rentals.length === 0 ? "Niciun contract de chirie — adaugă din modulul Chirii" : "Selectează adresă"} /></SelectTrigger>
                      <SelectContent>
                        {rentals.map((r) => {
                          const cap = r.capacitate_persoane || 0;
                          const label = r.titlu ? `${r.titlu} — ${r.adresa}` : r.adresa;
                          return <SelectItem key={r.id} value={r.id}>{label} · {r.nr_camere || 0} cam{cap ? ` · cap. ${cap}` : ""}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.cazare_type === "other" && (
                  <div className="md:col-span-2">
                    <Label>Adresa Cazare / Cameră<ReqMark/></Label>
                    <Input className={errBorder(isEmpty(form.adresa_cazare))} value={form.adresa_cazare} onChange={(e) => setF("adresa_cazare", e.target.value)} />
                  </div>
                )}
                {form.cazare_type === "own" && (
                  <div className="md:col-span-2 text-xs text-zinc-500 italic">
                    Persoana folosește adresa proprie (din câmpul „Adresă").
                  </div>
                )}
                <div><Label>Chei Cazare (0-4)</Label><Input type="number" min={0} max={4} value={form.chei_cazare} onChange={(e) => setF("chei_cazare", Math.max(0, Math.min(4, parseInt(e.target.value || "0"))))} /></div>

                <div className="md:col-span-2 pt-2 border-t border-[var(--border)]">
                  <div className="small-label mb-2">Bilă Roșie (până la 3 intrări)</div>
                </div>
                {[0, 1, 2].map((i) => {
                  const val = (form.bila_rosie || [])[i] || "";
                  const filled = val.trim().length > 0;
                  return (
                    <div key={i} className={`md:col-span-2 p-3 rounded-md border ${filled ? "rubric-red" : "border-[var(--border)] bg-white"}`}>
                      <div className="flex items-center gap-2">
                        <span className={`bila-dot ${filled ? "bila-red bila-pulse" : "bila-green"}`} />
                        <Label className="small-label">Bilă Roșie #{i + 1}</Label>
                      </div>
                      <Textarea
                        className="mt-2 bg-white"
                        rows={2}
                        value={val}
                        onChange={(e) => {
                          const curr = [...(form.bila_rosie || ["", "", ""])];
                          while (curr.length < 3) curr.push("");
                          curr[i] = e.target.value;
                          setF("bila_rosie", curr);
                        }}
                        placeholder={`Detalii bilă roșie ${i + 1}...`}
                      />
                    </div>
                  );
                })}

                <div className="md:col-span-2 pt-2 border-t border-[var(--border)]">
                  <div className="small-label mb-2">Echipament & Alte informații</div>
                </div>
                <div className="md:col-span-2">
                  <PSAAssignmentPanel personId={form.id} personName={`${form.nume || ""} ${form.prenume || ""}`.trim()} />
                </div>
                <div className="md:col-span-2"><Label>Alte Detalii</Label><Textarea rows={3} value={form.alte_detalii} onChange={(e) => setF("alte_detalii", e.target.value)} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
                <Button data-testid="submit-person" onClick={submit} className="bg-[var(--primary)]">Salvează</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="flex items-center gap-2 mb-3 flex-wrap card-flat px-3 py-2" data-testid="salary-month-bar">
          <span className="text-xs font-semibold text-zinc-500 uppercase">💰 Plată salariu pentru luna:</span>
          <Input type="month" value={salaryMonth} onChange={(e) => setSalaryMonth(e.target.value)} className="h-8 max-w-[180px]" data-testid="salary-month-input"/>
          <span className="text-[11px] text-zinc-500">Click pe bulină pe card: 🔴 neplatit → 🟡 parțial → 🟢 plătit</span>
        </div>
      )}

      {isAdmin && noRateCount > 0 && (
        <div
          className="flex items-center gap-3 mb-3 p-3 rounded-md border-2 border-amber-400 bg-amber-50"
          data-testid="no-rate-summary-banner"
        >
          <span className="text-2xl leading-none">⚠</span>
          <div className="flex-1">
            <div className="font-bold text-amber-900 text-sm">
              {noRateCount} {noRateCount === 1 ? "persoană activă fără tarif orar" : "persoane active fără tarif orar"}
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              Orele logate pentru aceste persoane vor avea <strong>Salariu = 0 CHF</strong> (doar diurnă). Setează tariful în profilul fiecăreia.
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setNoRateFilter((v) => !v); setFirmaFilter("all"); }}
            className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-colors ${noRateFilter ? "bg-amber-700 text-white hover:bg-amber-800" : "bg-amber-100 text-amber-900 border border-amber-400 hover:bg-amber-200"}`}
            data-testid="toggle-no-rate-filter"
          >
            {noRateFilter ? "✗ Arată toate" : "Vezi lista →"}
          </button>
        </div>
      )}

      {employerCompanies.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="firma-filter-bar">
          <button
            onClick={() => setFirmaFilter("all")}
            data-testid="firma-filter-all"
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${firmaFilter === "all" ? "bg-[var(--primary)] text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
          >
            Toate <span className="opacity-70">({firmaCounts.all})</span>
          </button>
          {employerCompanies.filter((c) => c.active !== false).map((c) => (
            <button
              key={c.id}
              onClick={() => setFirmaFilter(c.id)}
              data-testid={`firma-filter-${c.id}`}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${firmaFilter === c.id ? "bg-[var(--primary)] text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
            >
              {c.name} <span className="opacity-70">({firmaCounts[c.id] || 0})</span>
            </button>
          ))}
          <button
            onClick={() => setFirmaFilter("none")}
            data-testid="firma-filter-none"
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${firmaFilter === "none" ? "bg-zinc-700 text-white" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}
          >
            Fără firmă <span className="opacity-70">({firmaCounts.none})</span>
          </button>
          {(() => {
            const ekasCount = rows.filter((r) => r.ekas).length;
            const licCount = rows.filter((r) => r.permis_conducere).length;
            return (
              <>
                <button
                  onClick={() => setEkasFilter((v) => !v)}
                  data-testid="ekas-filter-toggle"
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors inline-flex items-center gap-1 ${ekasFilter ? "bg-yellow-500 text-white" : "bg-yellow-50 text-yellow-900 border border-yellow-300 hover:bg-yellow-100"}`}
                  title="Doar persoane cu certificare EKAS"
                >
                  <span>⭐</span> EKAS <span className="opacity-70">({ekasCount})</span>
                </button>
                <button
                  onClick={() => setLicenseFilter((v) => !v)}
                  data-testid="license-filter-toggle"
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors inline-flex items-center gap-1 ${licenseFilter ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-900 border border-blue-300 hover:bg-blue-100"}`}
                  title="Doar persoane cu permis de conducere (șoferi)"
                >
                  <span>🚗</span> Șoferi <span className="opacity-70">({licCount})</span>
                </button>
              </>
            );
          })()}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-zinc-500 uppercase tracking-wider font-semibold">Ordonare</span>
            <Select value={sortMode} onValueChange={setSortMode}>
              <SelectTrigger className="h-8 text-xs w-[200px]" data-testid="sort-mode-select">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="convic_first">ConVic primii, apoi A→Z</SelectItem>
                <SelectItem value="name_asc">Nume A→Z</SelectItem>
                <SelectItem value="name_desc">Nume Z→A</SelectItem>
                <SelectItem value="firma">Grupat după firmă</SelectItem>
                <SelectItem value="hire_desc">Cei mai noi angajați</SelectItem>
                <SelectItem value="hire_asc">Cei mai vechi angajați</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {(() => {
        if (!isAdmin) return null;
        const list = permisExpiringSoon(rows, employerCompanies, 14);
        if (list.length === 0) return null;
        const expired = list.filter((x) => x.days_left < 0);
        const expiring = list.filter((x) => x.days_left >= 0);
        return (
          <div className={`mb-4 card-flat p-4 border-l-4 ${expired.length > 0 ? "border-l-red-500 bg-red-50" : "border-l-amber-500 bg-amber-50"}`} data-testid="permis-reminder-banner">
            <div className="flex items-start gap-3">
              <AlertCircle className={expired.length > 0 ? "text-red-600" : "text-amber-600"} size={22} />
              <div className="flex-1">
                <div className={`font-bold text-sm ${expired.length > 0 ? "text-red-700" : "text-amber-800"}`}>
                  {expired.length > 0
                    ? `${expired.length} permis(e) de ședere EXPIRAT(E) și ${expiring.length} expiră în ≤14 zile`
                    : `${expiring.length} permis(e) de ședere expiră în ≤14 zile`}
                </div>
                <div className="text-xs text-zinc-700 mt-1">Acțiune necesară — încarcă noul permis sau actualizează data expirării.</div>
                <ul className="mt-2 space-y-0.5 text-xs">
                  {list.slice(0, 10).map((x) => (
                    <li key={x.person_id} className="flex items-center gap-2">
                      <span className={x.days_left < 0 ? "text-red-700 font-bold" : "text-amber-800"}>
                        {x.days_left < 0 ? `⛔ EXPIRAT acum ${-x.days_left} zile` : `⚠ ${x.days_left} zile rămase`}
                      </span>
                      <Link to={`/personal/${x.person_id}#documente`} className="font-medium hover:underline" data-testid={`permis-reminder-link-${x.person_id}`}>
                        {x.person_name}
                      </Link>
                      <span className="text-zinc-500">(expiră {x.expires_at})</span>
                    </li>
                  ))}
                  {list.length > 10 && <li className="text-zinc-500 italic">... și încă {list.length - 10}</li>}
                </ul>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="flex items-center gap-3 mb-4">
        <div className="card-flat p-4 flex-1 flex items-center gap-2">
          <Search size={16} className="text-zinc-400" />
          <Input
            placeholder="Caută după nume, prenume sau telefon..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="flex items-center gap-1 border border-[var(--border)] rounded-md p-1 bg-white shadow-sm flex-shrink-0" data-testid="personal-view-toggle">
          <button
            onClick={() => { setViewMode("cards"); localStorage.setItem("convic_persons_view", "cards"); }}
            className={`text-xs px-3 py-1.5 rounded font-semibold transition-colors flex items-center gap-1.5 ${viewMode === "cards" ? "bg-[var(--primary)] text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
            data-testid="personal-view-cards"
            title="Vizualizare carduri"
          >
            <LayoutGrid size={14}/> Carduri
          </button>
          <button
            onClick={() => { setViewMode("list"); localStorage.setItem("convic_persons_view", "list"); }}
            className={`text-xs px-3 py-1.5 rounded font-semibold transition-colors flex items-center gap-1.5 ${viewMode === "list" ? "bg-[var(--primary)] text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
            data-testid="personal-view-list"
            title="Vizualizare listă"
          >
            <ListIcon size={14}/> Listă
          </button>
        </div>
      </div>

      {(() => {
        // PAS 1: Split top-level — Activi vs Inactivi (concediu medical) vs Concediați (oficial)
        const activePersons = sortedFiltered.filter((p) => p.active !== false);
        // Inactivi = active===false dar fără data_concedierii (ex: concediu medical, suspendare temporară)
        const inactivePersons = sortedFiltered.filter((p) => p.active === false && !p.data_concedierii);
        // Concediați = active===false ȘI data_concedierii setată (concediere oficială)
        const dismissedPersons = sortedFiltered.filter((p) => p.active === false && !!p.data_concedierii);

        // Grupare pe categorii (doar pentru ACTIVI): Admini → Mecanici/Magazinieri → Restul
        const getRoleBucket = (p) => {
          const u = users.find((x) => x.person_id === p.id);
          const role = u?.role;
          if (role === "admin" || role === "planificator") return "admin";
          if (role === "magazioner") return "mecanic";
          return "team";
        };
        const groups = { admin: [], mecanic: [], team: [] };
        for (const p of activePersons) groups[getRoleBucket(p)].push(p);
        const sections = [
          { key: "admin", icon: "👑", label: "Administratori & Planificatori", colorBar: "border-l-violet-500", items: groups.admin },
          { key: "mecanic", icon: "🔧", label: "Mecanici / Magazinieri", colorBar: "border-l-amber-500", items: groups.mecanic },
          { key: "team", icon: "👷", label: "Echipă", colorBar: "border-l-emerald-500", items: groups.team },
        ];
        // Secțiunea „Inactivi" (concediu medical / suspendare temporară)
        if (inactivePersons.length > 0) {
          sections.push({
            key: "inactive",
            icon: "⏸️",
            label: "Inactivi (concediu medical / suspendare temporară)",
            colorBar: "border-l-amber-500",
            items: inactivePersons,
            isInactive: true,
            isSeparated: true,
          });
        }
        // Secțiunea „Concediați" (oficial, cu data_concedierii)
        if (dismissedPersons.length > 0) {
          sections.push({
            key: "dismissed",
            icon: "🚪",
            label: "Concediați (oficial)",
            colorBar: "border-l-zinc-500",
            items: dismissedPersons,
            isDismissed: true,
            isSeparated: true,
          });
        }
        // Helper: clasifică o persoană inactivă/concediată în 3 stări (pentru badge-uri)
        const classifyHrStatus = (p) => {
          const a = auditCoverage[p.id];
          const psaCount = a?.psa_count;
          const handovers = a?.last_handovers || [];
          const hasUnreturned = typeof psaCount === "number" && psaCount > 0;
          const everReceived = handovers.length > 0 || hasUnreturned;
          if (hasUnreturned) return "unreturned";
          if (!everReceived) return "never_received";
          return "handed_over";
        };
        return sections.map((sec) => sec.items.length === 0 ? null : (
          <div key={sec.key} className={`mb-6 ${sec.isSeparated ? "mt-10 pt-6 border-t-4 border-dashed border-zinc-300" : ""}`} data-testid={`person-section-${sec.key}`}>
            <h3 className={`font-display font-bold text-base mb-3 pl-3 border-l-4 ${sec.colorBar} flex items-center gap-2 flex-wrap`}>
              <span>{sec.icon}</span>
              {sec.label}
              <span className="text-zinc-400 text-xs font-normal">· {sec.items.length}</span>
              {(sec.isDismissed || sec.isInactive) && (() => {
                const counts = { unreturned: 0, handed_over: 0, never_received: 0 };
                for (const p of sec.items) counts[classifyHrStatus(p)] += 1;
                return (
                  <span className="text-[11px] font-semibold ml-2 inline-flex items-center gap-2 flex-wrap">
                    {counts.unreturned > 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300" data-testid={`hr-badge-unreturned-${sec.key}`}>⚠ {counts.unreturned} cu echipament neîntors</span>}
                    {counts.handed_over > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`hr-badge-handed-over-${sec.key}`}>✓ {counts.handed_over} predat complet</span>}
                    {counts.never_received > 0 && <span className="px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-300" data-testid={`hr-badge-never-received-${sec.key}`}>○ {counts.never_received} fără PSA primit</span>}
                  </span>
                );
              })()}
            </h3>
            <div className={viewMode === "list" ? "card-flat p-0 overflow-hidden" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"}>
              {viewMode === "list" && (
                <div className="grid grid-cols-[auto_minmax(0,1fr)_140px_160px_90px_90px] gap-3 px-4 py-2 bg-zinc-50 border-b border-[var(--border)] text-[11px] font-bold uppercase tracking-wider text-zinc-600">
                  <span></span>
                  <span>Nume</span>
                  <span>Firmă</span>
                  <span>Telefon</span>
                  <span>Cont</span>
                  <span className="text-right">Acțiuni</span>
                </div>
              )}
              {sec.items.map((p) => viewMode === "list" ? (
                <PersonListRow
                  key={p.id}
                  p={p}
                  users={users}
                  employerCompanies={employerCompanies}
                  salaryPayments={salaryPayments}
                  isAdmin={isAdmin}
                  canDo={canDo}
                  toggleActive={toggleActive}
                  removePerson={removePerson}
                />
              ) : (() => {
                // Precompute missing — used both pentru styling card-level și banner intern
                const _missing = missingPersonFields(p);
                const _missingDocs = missingPersonDocs(p, employerCompanies);
                const _audit = auditCoverage[p.id];
                const _missingAudit = (_audit?.missing_months || []).length;
                const _missingSlips = (_audit?.missing_slips || []).length;
                const _slipMismatches = (_audit?.slip_mismatches || []).length;
                const _noPsa = _audit && _audit.psa_count === 0 && p.active !== false;
                const _isDismissed = p.active === false;
                const _dismissedPsaCount = _isDismissed ? (_audit?.psa_count ?? null) : null;
                const _handoversCount = (_audit?.last_handovers || []).length;
                const _everReceived = _handoversCount > 0 || (_dismissedPsaCount || 0) > 0;
                const _dismissedRed = _isDismissed && (_dismissedPsaCount || 0) > 0;
                // Green doar dacă a primit ceva și a returnat tot. Dacă n-a primit niciodată → neutru.
                const _dismissedGreen = _isDismissed && _dismissedPsaCount === 0 && _everReceived;
                const _dismissedNeverReceived = _isDismissed && _dismissedPsaCount === 0 && !_everReceived;
                const _totalErrors = _missing.length + _missingDocs.length + _missingAudit + _missingSlips + _slipMismatches + (_noPsa ? 1 : 0);
                const _hasAny = _totalErrors > 0;
                const _hasCritical = _missingDocs.length > 0 || _missing.length >= 3 || _missingAudit >= 3 || _missingSlips > 0 || _slipMismatches > 0 || _noPsa;
                const _cardCls = _dismissedRed
                  ? "card-flat p-5 hover:border-red-600 transition-colors block relative group border-2 border-red-500 bg-red-50/40 ring-2 ring-red-200 animate-pulse-red-soft"
                  : _dismissedGreen
                    ? "card-flat p-5 hover:border-emerald-600 transition-colors block relative group border-2 border-emerald-400 bg-emerald-50/40"
                    : _dismissedNeverReceived
                      ? "card-flat p-5 hover:border-zinc-500 transition-colors block relative group border-2 border-zinc-300 bg-zinc-50/60 opacity-90"
                      : _hasCritical
                      ? "card-flat p-5 hover:border-[var(--primary)] transition-colors block relative group border-l-[8px] border-l-red-600 ring-2 ring-red-300 animate-pulse-red-soft"
                      : _hasAny
                        ? "card-flat p-5 hover:border-[var(--primary)] transition-colors block relative group border-l-[6px] border-l-amber-500"
                        : "card-flat p-5 hover:border-[var(--primary)] transition-colors block relative group";
                return (
          <Link
            key={p.id}
            to={`/personal/${p.id}`}
            data-testid={`person-card-${p.id}`}
            className={_cardCls}
          >
            {/* Badge colț stânga-sus: cont activ + a logat măcar o dată ===================== */}
            {(() => {
              const _userAccounts = users.filter((x) => x.person_id === p.id);
              if (_userAccounts.length === 0) return null; // nu are niciun cont
              // Logat = oricare dintre conturile lui a logat măcar o dată
              const _loggedAccount = _userAccounts.find((x) => x.last_login_at || x.first_login_at);
              if (!_loggedAccount) {
                return (
                  <div
                    className="absolute top-2 left-2 z-20 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-200 text-zinc-500 text-[9px] font-bold uppercase tracking-wider border border-zinc-300"
                    title={`${_userAccounts.length} cont(uri) create dar niciun login încă`}
                    data-testid={`person-login-status-${p.id}`}
                  >
                    <span className="text-[10px] leading-none">○</span>
                    <span className="hidden sm:inline">Nelogat</span>
                  </div>
                );
              }
              const _totalLogins = _userAccounts.reduce((s, u) => s + (u.login_count || 0), 0);
              const _lastTs = _loggedAccount.last_login_at;
              return (
                <div
                  className="absolute top-2 left-2 z-20 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider border border-emerald-300"
                  title={`Logat${_lastTs ? ` ultima dată ${new Date(_lastTs).toLocaleDateString("ro-RO")}` : ""}${_totalLogins ? ` · ${_totalLogins} logări totale` : ""}`}
                  data-testid={`person-login-status-${p.id}`}
                >
                  <span className="text-[10px] leading-none">✓</span>
                  <span className="hidden sm:inline">Logat</span>
                </div>
              );
            })()}
            {/* Pulsing red badge with total error count — vizibil mereu pe cardul cu erori */}
            {_hasAny && (
              <div
                className={`absolute top-2 right-2 z-20 px-2 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider shadow-md flex items-center gap-1 ${_hasCritical ? "bg-red-600 text-white animate-pulse" : "bg-amber-500 text-white"}`}
                data-testid={`person-errors-badge-${p.id}`}
                title={[
                  _missing.length > 0 ? `${_missing.length} date personale lipsă` : null,
                  _missingDocs.length > 0 ? `${_missingDocs.length} documente critice lipsă` : null,
                  _missingAudit > 0 ? `${_missingAudit} rapoarte lunare lipsă` : null,
                  _missingSlips > 0 ? `${_missingSlips} fișe de salariu lipsă` : null,
                  _slipMismatches > 0 ? `${_slipMismatches} discrepanțe raport↔fișa salariu` : null,
                  _noPsa ? "PSA nealocat" : null,
                ].filter(Boolean).join(" · ")}
              >
                <span className="text-sm leading-none">⚠</span>
                <span className="tabular-nums">{_totalErrors}</span>
                <span className="hidden sm:inline">{_totalErrors === 1 ? "eroare" : "erori"}</span>
              </div>
            )}
            {/* Quick actions floating top-right (visible on hover) */}
            {(canDo("PERSONAL", "edit") || canDo("PERSONAL", "delete")) && (
              <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10" data-testid={`quick-actions-${p.id}`}>
                {canDo("PERSONAL", "edit") && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleActive(p); }}
                    className={`p-1.5 rounded-full bg-white shadow-sm border border-zinc-200 hover:scale-110 transition-transform ${p.active === false ? "text-green-600 hover:border-green-400" : "text-amber-600 hover:border-amber-400"}`}
                    title={p.active === false ? "Reactivează" : "Dezactivează"}
                    data-testid={`toggle-active-${p.id}`}
                  >
                    <Power size={14}/>
                  </button>
                )}
                {canDo("PERSONAL", "delete") && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); removePerson(p); }}
                    className="p-1.5 rounded-full bg-white shadow-sm border border-zinc-200 text-red-600 hover:border-red-400 hover:scale-110 transition-transform"
                    title="Șterge definitiv"
                    data-testid={`delete-person-${p.id}`}
                  >
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center gap-4">
              {p.photo_file_id ? (
                <AuthImg fileId={p.photo_file_id} className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-zinc-100 grid place-items-center text-zinc-500">
                  <UserRound size={22} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {isAdmin && (() => {
                    const pay = salaryPayments.find((x) => x.person_id === p.id);
                    const status = pay?.status || "unpaid";
                    const cfg = {
                      paid: { color: "#10B981", bg: "bg-[var(--green-bg)]", title: "Salariu plătit total — click pentru schimbare" },
                      partial: { color: "#F59E0B", bg: "bg-amber-100", title: "Salariu plătit parțial — click pentru schimbare" },
                      unpaid: { color: "#DC2626", bg: "bg-red-100", title: "Salariu nepaltit — click pentru schimbare" },
                    }[status];
                    return (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); cyclePayment(p.id); }}
                        className={`w-4 h-4 rounded-full flex-shrink-0 hover:scale-125 transition-transform ${cfg.bg}`}
                        style={{ background: cfg.color }}
                        title={cfg.title}
                        data-testid={`pay-dot-${p.id}`}
                      />
                    );
                  })()}
                  <div className="font-display font-bold text-lg truncate flex items-center gap-1.5">
                    {p.nume} {p.prenume}
                  </div>
                  {/* Status online/offline — vizibil mereu (label cu „acum 5m" / „ieri 14:30") */}
                  <OnlineBadge personId={p.id} size={8} label className="mt-0.5"/>
                  {p.active !== false && !p.data_concedierii && (p.hourly_rate == null || Number(p.hourly_rate) <= 0) && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 flex-shrink-0"
                      title="Tarif orar nesetat — orele logate vor avea Salariu = 0 CHF"
                      data-testid={`person-no-rate-${p.id}`}
                    >
                      ⚠ Fără tarif
                    </span>
                  )}
                  {(() => {
                    const list = Array.isArray(p.bila_rosie) ? p.bila_rosie : (p.bila_rosie ? [p.bila_rosie] : []);
                    const count = list.filter((x) => x && String(x).trim()).length;
                    if (count === 0) return null;
                    return (
                      <div className="flex items-center gap-1" data-testid={`bila-rosie-count-${p.id}`} title={`${count} Bilă Roșie`}>
                        {Array.from({ length: count }).map((_, i) => (
                          <span key={i} className="bila-dot bila-red bila-pulse" />
                        ))}
                      </div>
                    );
                  })()}
                </div>
                <div className="text-xs text-zinc-500 mt-1 truncate">{p.telefon || "—"}</div>
                {p.current_cars?.length > 0 && (
                  <div className="text-xs text-emerald-700 mt-1 truncate flex items-center gap-1" data-testid={`person-current-car-${p.id}`} title="Autoturism alocat azi">
                    <span className="text-zinc-400">🚗 Azi:</span>
                    <span className="font-medium truncate">{p.current_cars.map((c) => `${c.marca || ""} ${c.model || ""} (${c.numar_inmatriculare || c.label || "—"})`.trim()).join(", ")}</span>
                  </div>
                )}
                {(() => {
                  const firm = employerCompanies.find((c) => c.id === p.firma_id);
                  return firm ? (
                    <div className="text-xs text-zinc-700 mt-1 truncate" data-testid={`person-firma-${p.id}`}>
                      <span className="text-zinc-400">Firma:</span> <span className="font-medium">{firm.name}</span>
                    </div>
                  ) : null;
                })()}
                <div className="flex items-center gap-2 mt-2">
                  {p.active === false ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">Inactiv</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--green-bg)] text-[var(--green-valid)]">Activ</span>
                  )}
                  {p.permis_conducere && <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--primary-subtle)] text-[var(--primary)] inline-flex items-center gap-1" title="Permis de conducere"><span>🚗</span> Permis</span>}
                  {p.ekas && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300 inline-flex items-center gap-1" title="Certificare EKAS" data-testid={`ekas-badge-${p.id}`}><span className="text-yellow-500">⭐</span> EKAS</span>}
                  {p.limba_germana && <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-200 inline-flex items-center gap-1" title="Vorbește limba germană" data-testid={`german-badge-${p.id}`}><span>🇩🇪</span> Germană</span>}
                  {(() => {
                    if (p.active === false) return null;
                    const psaCount = auditCoverage[p.id]?.psa_count;
                    // Pulsing red dacă person activă și PSA = 0; verde compact dacă > 0
                    if (psaCount === 0) {
                      return (
                        <Link
                          to={`/personal/${p.id}#psa`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 border-2 border-red-500 bg-red-100 text-red-800 font-extrabold animate-pulse uppercase tracking-wider"
                          title="Nu are PSA alocat — predă echipament urgent"
                          data-testid={`psa-no-badge-${p.id}`}
                        >
                          <span>🦺</span> PSA 0
                        </Link>
                      );
                    }
                    if (typeof psaCount === "number" && psaCount > 0) {
                      return (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1"
                          title={`${psaCount} echipament${psaCount === 1 ? "" : "e"} PSA alocat${psaCount === 1 ? "" : "e"}`}
                          data-testid={`psa-has-badge-${p.id}`}
                        >
                          <span>🦺</span> {psaCount} PSA
                        </span>
                      );
                    }
                    return null;
                  })()}
                  {p.is_subcontractor && (() => {
                    const ms = meldungenSummary[p.id];
                    const remaining = ms ? ms.remaining_days : 90;
                    const low = remaining < 10;
                    return (
                      <Link
                        to={`/personal/${p.id}#meldeverfahren`}
                        onClick={(e) => e.stopPropagation()}
                        className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 border ${low ? "bg-red-100 text-red-800 border-red-300 animate-pulse font-bold" : "bg-blue-50 text-blue-800 border-blue-200"}`}
                        title={`Meldeverfahren ${new Date().getFullYear()}: ${remaining}/90 zile lucrătoare rămase (folosite ${ms?.used_days ?? 0})${low ? " — ATENȚIE: aproape de limită!" : ""}`}
                        data-testid={`meldung-badge-${p.id}`}
                      >
                        <span>🌍</span> {remaining}/90 zile{low ? " ⚠" : ""}
                      </Link>
                    );
                  })()}
                  {isAdmin && (() => {
                    const u = users.find((x) => x.person_id === p.id);
                    if (!u) return (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500 inline-flex items-center gap-1" title="Fără cont de utilizator" data-testid={`no-account-${p.id}`}>
                        Fără cont
                      </span>
                    );
                    if (u.active === false) return (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1" title={`Cont dezactivat: ${u.username}`} data-testid={`account-disabled-${p.id}`}>
                        <KeyRound size={11}/> Cont oprit
                      </span>
                    );
                    return (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--primary-subtle)] text-[var(--primary)] inline-flex items-center gap-1 font-mono" title={`Username: ${u.username}`} data-testid={`account-active-${p.id}`}>
                        <KeyRound size={11}/> {u.username}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
            <PersonAlertBanners
              p={p}
              audit={_audit}
              missing={_missing}
              missingDocs={_missingDocs}
            />
          </Link>
                );
              })()
              )}
            </div>
          </div>
        ));
      })()}
        {filtered.length === 0 && <div className="text-sm text-zinc-500">Nicio persoană găsită.</div>}

      <MeldungBulkUploadDialog
        open={bulkUploadOpen}
        onOpenChange={setBulkUploadOpen}
        persons={rows}
        onSaved={() => load()}
      />
    </div>
  );
}

// ---------- List row (compact single-line view) ----------
function PersonListRow({ p, users, employerCompanies, salaryPayments, isAdmin, canDo, toggleActive, removePerson }) {
  const u = users.find((x) => x.person_id === p.id);
  const firma = employerCompanies.find((f) => f.id === p.firma_id);
  const pay = isAdmin ? salaryPayments.find((x) => x.person_id === p.id) : null;
  const payStatus = pay?.status || "unpaid";
  const payColor = { paid: "#10B981", partial: "#F59E0B", unpaid: "#DC2626" }[payStatus] || "#94a3b8";

  return (
    <Link
      to={`/personal/${p.id}`}
      data-testid={`person-row-${p.id}`}
      className={`grid grid-cols-[auto_minmax(0,1fr)_140px_160px_90px_90px] gap-3 px-4 py-2.5 items-center border-b border-zinc-100 hover:bg-zinc-50 transition-colors text-sm ${p.active === false ? "opacity-60" : ""}`}
    >
      <div className="relative">
        {p.photo_file_id ? (
          <AuthImg fileId={p.photo_file_id} className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-zinc-100 grid place-items-center text-zinc-500">
            <UserRound size={16} />
          </div>
        )}
        {isAdmin && (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white"
            style={{ background: payColor }}
            title={payStatus === "paid" ? "Salariu plătit" : payStatus === "partial" ? "Plătit parțial" : "Nepaltit"}
          />
        )}
      </div>
      <div className="min-w-0">
        <div className="font-semibold truncate flex items-center gap-2">
          {p.prenume} {p.nume}
          <OnlineBadge personId={p.id} size={8}/>
          {p.active === false && (
            <span className="text-[9px] font-bold uppercase bg-zinc-200 text-zinc-600 px-1.5 py-0.5 rounded">Inactiv</span>
          )}
        </div>
        {p.adresa_cazare && <div className="text-[11px] text-zinc-500 truncate">📍 {p.adresa_cazare}</div>}
      </div>
      <div className="text-xs text-zinc-700 truncate">
        {firma ? firma.name : <span className="text-zinc-400 italic">Fără firmă</span>}
      </div>
      <div className="text-xs text-zinc-700 font-mono truncate">
        {p.telefon || <span className="text-zinc-400">—</span>}
      </div>
      <div>
        {!u ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">Fără cont</span>
        ) : u.active === false ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1">
            <KeyRound size={10}/> Oprit
          </span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--primary-subtle)] text-[var(--primary)] inline-flex items-center gap-1 font-mono truncate">
            <KeyRound size={10}/> {u.username}
          </span>
        )}
      </div>
      <div className="flex items-center justify-end gap-1">
        {canDo("PERSONAL", "edit") && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleActive(p); }}
            className={`p-1.5 rounded hover:bg-zinc-200 ${p.active === false ? "text-green-600" : "text-amber-600"}`}
            title={p.active === false ? "Reactivează" : "Dezactivează"}
            data-testid={`row-toggle-active-${p.id}`}
          >
            <Power size={14}/>
          </button>
        )}
        {canDo("PERSONAL", "delete") && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); removePerson(p); }}
            className="p-1.5 rounded text-red-600 hover:bg-red-50"
            title="Șterge definitiv"
            data-testid={`row-delete-person-${p.id}`}
          >
            <Trash2 size={14}/>
          </button>
        )}
      </div>
    </Link>
  );
}

